import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef } from "react";
import { MDSIconButton, MDSIconButtonSize } from "@/mds/components/buttons";
import { MDSIconKind } from "@/mds/components/icon";
import { MDSTextColor, MDSTextVariant } from "@/mds/components/text";
import { MDSTextFieldVariant } from "@/mds/components/forms/text-field/text-field.types";
import { FindState } from "./pdf-find-store";
import type { PdfFindStore } from "./pdf-find-store";
import {
  StyledFindBarContainer,
  StyledFindBarExpanding,
  StyledFindBarInput,
  StyledMatchCounterText,
} from "./pdf-find-bar.styles";
import { useHotkey } from "@/domains/hotkeys/use-hotkey";
import { resolveHotkey } from "@/modules/keyboard-shortcuts/platform";
import { KEYBOARD_SHORTCUT_BINDINGS } from "@/modules/keyboard-shortcuts/registry";

interface PdfFindBarProps {
  store: PdfFindStore;
}

export const PdfFindBar = observer(({ store }: PdfFindBarProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (store.isOpen) {
      // Small delay to ensure the input is mounted before focusing
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [store.isOpen]);

  const hotkeyOptions = {
    enabled: store.isOpen,
    target: inputRef,
  };

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.pdf_find_next_result),
    () => {
      store.findNext();
    },
    hotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.pdf_find_previous_result),
    () => {
      store.findPrevious();
    },
    hotkeyOptions
  );

  useHotkey(
    resolveHotkey(KEYBOARD_SHORTCUT_BINDINGS.escape_to_close),
    () => {
      store.close();
    },
    hotkeyOptions
  );

  const handleInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Keep this tiny input-local fallback instead of relying only on
      // useHotkey(): the input-targeted registrations are attached after mount,
      // so an immediate keypress right after opening the find bar can miss the
      // hotkey handler. These focused-input cases need to work on the first
      // event even before the hotkey library finishes registering.
      const isFindShortcut =
        event.key.toLowerCase() === "f" && (event.metaKey || event.ctrlKey) && !event.altKey;
      const isFindNextShortcut =
        event.key.toLowerCase() === "g" &&
        (event.metaKey || event.ctrlKey) &&
        !event.altKey &&
        !event.shiftKey;

      if (isFindShortcut) {
        event.preventDefault();
        event.stopPropagation();
        if (event.currentTarget.value.length > 0) {
          event.currentTarget.select();
        }
        return;
      }

      if (isFindNextShortcut) {
        event.preventDefault();
        event.stopPropagation();
        store.findNext();
        return;
      }

      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        store.findNext();
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        store.findPrevious();
        return;
      }

      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      store.close();
    },
    [store]
  );

  if (!store.isOpen) return null;

  const hasQuery = store.query.length > 0;
  const isNotFound = store.state === FindState.NOT_FOUND && hasQuery;
  const hasMatches = store.matchCount.total > 0;

  let matchText = "";
  if (isNotFound) {
    matchText = "Not found";
  } else if (hasQuery && hasMatches) {
    matchText = `${store.matchCount.current} of ${store.matchCount.total}`;
  }

  return (
    <StyledFindBarContainer data-test-id="pdf-find-bar">
      <StyledFindBarExpanding>
        <StyledFindBarInput
          autoFocus
          ref={inputRef}
          variant={MDSTextFieldVariant.Filter}
          placeholder="Find in PDF"
          value={store.query}
          onChange={store.setQuery}
          onKeyDown={handleInputKeyDown}
          aria-label="Find in PDF"
        />
      </StyledFindBarExpanding>
      {matchText && (
        <StyledMatchCounterText
          color={MDSTextColor.Tertiary}
          variant={MDSTextVariant.TextS}
          data-test-id="pdf-find-match-count"
        >
          {matchText}
        </StyledMatchCounterText>
      )}
      <MDSIconButton
        aria-label="Previous match"
        size={MDSIconButtonSize.Small}
        iconKind={MDSIconKind.AngleUp}
        isDisabled={!hasMatches}
        onClick={store.findPrevious}
      />
      <MDSIconButton
        aria-label="Next match"
        size={MDSIconButtonSize.Small}
        iconKind={MDSIconKind.AngleDown}
        isDisabled={!hasMatches}
        onClick={store.findNext}
      />
      <MDSIconButton
        aria-label="Close find bar"
        size={MDSIconButtonSize.Small}
        iconKind={MDSIconKind.Exit}
        onClick={store.close}
      />
    </StyledFindBarContainer>
  );
});
